import type { Dayjs } from 'dayjs';
import {
  createContext,
  type FC,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDepartments } from '@/common/useDepartments';
import type { Weekday_Window } from '@/generated/graphql/graphql';

const DRIVER_ID_PARAM = 'driverId';
const DEPARTMENT_ID_PARAM = 'departmentId';

export enum DistrictInsertType {
  SINGLE,
  WEEK,
  COLUMN,
  ALL,
}

export enum DistrictDeleteType {
  SINGLE,
  WEEK,
  COLUMN,
  ALL,
}

export interface CalendarState {
  loadingDistrictId: string | null;
  selectedDriverId: string | null;
  timeSlots: { id: number; timeDuration: string; weekday_windows: Weekday_Window[] }[];
  isEditMode: boolean;
  selectedTimeRange: [Dayjs, Dayjs] | null;
  selectedSlotId: number | null;
  selectedDistricts: string[] | null;
  selectedDayIndex: number | null;
  districtInsertType: DistrictInsertType;
  districtDeleteType: DistrictDeleteType;
}

const initialState: CalendarState = {
  loadingDistrictId: null,
  selectedDriverId: null,
  timeSlots: [],
  isEditMode: false,
  selectedTimeRange: null,
  selectedSlotId: null,
  selectedDistricts: null,
  selectedDayIndex: null,
  districtInsertType: DistrictInsertType.SINGLE,
  districtDeleteType: DistrictDeleteType.SINGLE,
};

const CalendarContext = createContext<
  | {
      state: CalendarState;
      setLoadingDistrictId: (loadingDistrictId: string | null) => void;
      setSelectedDriverId: (selectedDriverId: string | null) => void;
      setTimeSlots: (
        timeSlots: { id: number; timeDuration: string; weekday_windows: Weekday_Window[] }[]
      ) => void;
      setIsEditMode: (isEditMode: boolean) => void;
      setSelectedTimeRange: (selectedTimeRange: [Dayjs, Dayjs] | null) => void;
      setSelectedSlotId: (selectedSlotId: number | null) => void;
      setSelectedDistricts: (selectedDistrict: string[] | null) => void;
      setSelectedDayIndex: (selectedDayIndex: number | null) => void;
      setDistrictInsertType: (districtInsertType: DistrictInsertType) => void;
      setDistrictDeleteType: (districtDeleteType: DistrictDeleteType) => void;
    }
  | undefined
>(undefined);

export const CalendarProvider: FC<PropsWithChildren> = ({ children }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { departments, selectedDepartmentId, setSelectedDepartmentId } = useDepartments();

  const driverIdFromUrl = searchParams.get(DRIVER_ID_PARAM);
  const departmentIdFromUrl = searchParams.get(DEPARTMENT_ID_PARAM);

  const [state, setState] = useState<CalendarState>(() => ({
    ...initialState,
    selectedDriverId: driverIdFromUrl,
  }));

  const prevDepartmentIdRef = useRef<number>(selectedDepartmentId);
  const initialDepartmentIdFromUrlRef = useRef(departmentIdFromUrl);

  useEffect(() => {
    if (!departmentIdFromUrl) return;
    const parsed = Number(departmentIdFromUrl);
    if (!Number.isFinite(parsed)) return;
    const exists = departments.some((d) => d.id === parsed);
    if (!exists || parsed === selectedDepartmentId) return;
    setSelectedDepartmentId(parsed);
  }, [departmentIdFromUrl, departments, selectedDepartmentId, setSelectedDepartmentId]);

  useEffect(() => {
    if (prevDepartmentIdRef.current === selectedDepartmentId) {
      return;
    }

    const targetDepartmentId = initialDepartmentIdFromUrlRef.current
      ? Number(initialDepartmentIdFromUrlRef.current)
      : null;
    const isDeepLinkTransition = targetDepartmentId === selectedDepartmentId;

    if (!isDeepLinkTransition) {
      setState((prevState) => ({ ...prevState, selectedDriverId: null, timeSlots: [] }));
      setSearchParams(
        (prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.delete(DRIVER_ID_PARAM);
          newParams.delete(DEPARTMENT_ID_PARAM);
          return newParams;
        },
        { replace: true }
      );
    }

    prevDepartmentIdRef.current = selectedDepartmentId;
    initialDepartmentIdFromUrlRef.current = null;
  }, [selectedDepartmentId, setSearchParams]);

  const setLoadingDistrictId = useCallback((loadingDistrictId: string | null) => {
    setState((prevState) => ({ ...prevState, loadingDistrictId }));
  }, []);

  const setSelectedDriverId = useCallback(
    (selectedDriverId: string | null) => {
      setState((prevState) => ({ ...prevState, selectedDriverId }));
      setSearchParams(
        (prev) => {
          const newParams = new URLSearchParams(prev);
          if (selectedDriverId) {
            newParams.set(DRIVER_ID_PARAM, selectedDriverId);
            newParams.set(DEPARTMENT_ID_PARAM, String(selectedDepartmentId));
          } else {
            newParams.delete(DRIVER_ID_PARAM);
            newParams.delete(DEPARTMENT_ID_PARAM);
          }
          return newParams;
        },
        { replace: true }
      );
    },
    [setSearchParams, selectedDepartmentId]
  );

  useEffect(() => {
    setState((prevState) => {
      if (driverIdFromUrl !== prevState.selectedDriverId) {
        return { ...prevState, selectedDriverId: driverIdFromUrl };
      }
      return prevState;
    });
  }, [driverIdFromUrl]);

  const setTimeSlots = useCallback(
    (timeSlots: { id: number; timeDuration: string; weekday_windows: Weekday_Window[] }[]) => {
      setState((prevState) => ({ ...prevState, timeSlots }));
    },
    []
  );

  const setIsEditMode = useCallback((isEditMode: boolean) => {
    setState((prevState) => ({ ...prevState, isEditMode }));
  }, []);

  const setSelectedTimeRange = useCallback((selectedTimeRange: [Dayjs, Dayjs] | null) => {
    setState((prevState) => ({ ...prevState, selectedTimeRange }));
  }, []);

  const setSelectedSlotId = useCallback((selectedSlotId: number | null) => {
    setState((prevState) => ({ ...prevState, selectedSlotId }));
  }, []);

  const setSelectedDistricts = useCallback((selectedDistricts: string[] | null) => {
    setState((prevState) => ({ ...prevState, selectedDistricts }));
  }, []);

  const setSelectedDayIndex = useCallback((selectedDayIndex: number | null) => {
    setState((prevState) => ({ ...prevState, selectedDayIndex }));
  }, []);

  const setDistrictInsertType = useCallback((districtInsertType: DistrictInsertType) => {
    setState((prevState) => ({ ...prevState, districtInsertType }));
  }, []);

  const setDistrictDeleteType = useCallback((districtDeleteType: DistrictDeleteType) => {
    setState((prevState) => ({ ...prevState, districtDeleteType }));
  }, []);

  const contextValue = useMemo(
    () => ({
      state,
      setLoadingDistrictId,
      setSelectedDriverId,
      setTimeSlots,
      setIsEditMode,
      setSelectedTimeRange,
      setSelectedSlotId,
      setSelectedDistricts,
      setSelectedDayIndex,
      setDistrictInsertType,
      setDistrictDeleteType,
    }),
    [
      state,
      setLoadingDistrictId,
      setSelectedDriverId,
      setTimeSlots,
      setIsEditMode,
      setSelectedTimeRange,
      setSelectedSlotId,
      setSelectedDistricts,
      setSelectedDayIndex,
      setDistrictInsertType,
      setDistrictDeleteType,
    ]
  );

  return <CalendarContext.Provider value={contextValue}>{children}</CalendarContext.Provider>;
};

export const useCalendarState = () => {
  const context = useContext(CalendarContext);
  if (!context) {
    throw new Error('useCalendarState must be used within a CalendarProvider');
  }
  return context;
};
